import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle'
import { ProfileSelectOption } from 'components/ui/forms/course/components/forms/RequiredProfilesInput'
import { lmsProfilesService } from 'api/lms-profiles'
import { LmsProfileCourseKind } from 'api/lms-profiles/GetProfilesByResource'
import { CourseProfilesCard } from './CourseProfilesCard'
import styles from './styles.module.scss'

interface IProps {
  requiredProfiles: ProfileSelectOption[]
  optionalProfiles: ProfileSelectOption[]
  onChange: (next: {
    requiredProfiles: ProfileSelectOption[]
    optionalProfiles: ProfileSelectOption[]
  }) => void
  disabled?: boolean
  requiredError?: string
  optionalError?: string
  courseId?: number
}

/**
 * Selector de perfiles LMS para formulario de curso: dos cards (obligatorio / opcional).
 */
export const CourseProfilesField = ({
  requiredProfiles,
  optionalProfiles,
  onChange,
  disabled = false,
  requiredError,
  optionalError,
  courseId,
}: IProps) => {
  const { t } = useTranslation()
  const prefix = 'administration.courses.new.form.profilesField'
  const labelsPrefix = 'administration.courses.new.form.courseFields'

  const [restrictedByKind, setRestrictedByKind] = useState<
    Record<LmsProfileCourseKind, number>
  >({
    required: 0,
    optional: 0,
  })

  const fetchRestrictedCount = useCallback(
    async (kind: LmsProfileCourseKind) => {
      if (!courseId) return 0

      const { data } = await lmsProfilesService.getProfilesByResource(
        { id: courseId, type: 'course' },
        { page: 1, search: '', perPage: 1, kind }
      )

      return data.restrictedProfilesCount || 0
    },
    [courseId]
  )

  useEffect(() => {
    if (!courseId) {
      setRestrictedByKind({ required: 0, optional: 0 })
      return
    }

    let cancelled = false

    Promise.all([
      fetchRestrictedCount('required'),
      fetchRestrictedCount('optional'),
    ]).then(([required, optional]) => {
      if (!cancelled) {
        setRestrictedByKind({ required, optional })
      }
    })

    return () => {
      cancelled = true
    }
  }, [courseId, fetchRestrictedCount])

  const optionalExcludedIds = useMemo(
    () => new Set(requiredProfiles.map((profile) => String(profile.value))),
    [requiredProfiles]
  )

  const requiredExcludedIds = useMemo(
    () => new Set(optionalProfiles.map((profile) => String(profile.value))),
    [optionalProfiles]
  )

  const showRestrictedAlert =
    restrictedByKind.required + restrictedByKind.optional > 0

  return (
    <div className={styles.wrapper}>
      {showRestrictedAlert && (
        <div className={styles.restrictedAlert}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <p>{t(`${prefix}.restrictedAlert`)}</p>
        </div>
      )}

      <CourseProfilesCard
        label={t(`${labelsPrefix}.optionalProfiles.label`)}
        profiles={optionalProfiles}
        excludedProfileIds={optionalExcludedIds}
        restrictedProfilesCount={restrictedByKind.optional}
        disabled={disabled}
        error={optionalError}
        onChange={(profiles) =>
          onChange({ requiredProfiles, optionalProfiles: profiles })
        }
      />

      <CourseProfilesCard
        label={t(`${labelsPrefix}.requiredProfiles.label`)}
        profiles={requiredProfiles}
        excludedProfileIds={requiredExcludedIds}
        restrictedProfilesCount={restrictedByKind.required}
        disabled={disabled}
        error={requiredError}
        onChange={(profiles) =>
          onChange({ requiredProfiles: profiles, optionalProfiles })
        }
      />
    </div>
  )
}

export default CourseProfilesField
